import type { FunctionComponent } from 'react';
import { useCallback, useContext, useMemo } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { SmartLink, SmartLinkAnalyticsContext } from '@trello/smart-card';
import { boardUrlFromShortLink } from '@trello/urls';

import { trelloLinkHandler } from 'app/src/trelloLinkHandler';
import { useTemplatePreviewFragment } from './TemplatePreviewFragment.generated';

export const TemplatePreview: FunctionComponent<{
  boardId: string;
}> = ({ boardId }) => {
  const analyticsContext = useContext(SmartLinkAnalyticsContext);

  const onTemplatePreviewClick = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'embed',
      actionSubjectId: 'templatePreview',
      source: 'templateStoryPageScreen',
    });
  }, []);

  const { data: board } = useTemplatePreviewFragment({
    from: { id: boardId },
    optimistic: true,
  });

  const shortLink = board?.shortLink || '';
  const name = board?.name || '';

  const normalizedUrl = useMemo(() => {
    return `${window.location.origin}${boardUrlFromShortLink(
      shortLink,
      name,
    )}?isPreviewingTemplate=false&keepParams=true`;
  }, [name, shortLink]);

  // If url fails to resolve, render preview as a plain link.
  const plainLink = useCallback(
    () => (
      <a href={normalizedUrl} target="_blank" draggable="false">
        {normalizedUrl}
      </a>
    ),
    [normalizedUrl],
  );

  return (
    <SmartLink
      key={normalizedUrl}
      url={normalizedUrl}
      plainLink={plainLink}
      showFrame={false}
      appearance="embed"
      handleTrelloLinkClick={trelloLinkHandler}
      analyticsContext={analyticsContext}
      onClick={onTemplatePreviewClick}
    />
  );
};
